// Positionen der Bestellung im Arbeitsplatz (21.10.2026 Schnitt 3): unter der Aufgabe der
// Bestellspur. Je Position Menge/EK, Hinweise, Bedarf → Grundartikel und Loeschen — mit denselben
// Bausteinen wie der Reiter Positionen der Akte; Loeschen hier nur nach Rueckfrage.
import { GrundartikelLink } from "@/components/catalog/GrundartikelLink";
import { AusverkaufAbzeichen } from '@/components/AusverkaufAbzeichen';
import { useState } from "react";
import type { PurchaseOrderFile, PurchaseOrderItem } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { formatUsd } from "@/lib/purchase-format";
import { jeEinheit, mengeMitEinheit } from "../einheit";
import {
  AnforderungAnGaKnopf, PositionLoeschenKnopf, PositionMengeEk, PositionNotiz, positionsName,
} from "../PositionAktionen";

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full border border-line px-1.5 py-px text-[10px] text-ink-2">{children}</span>;
}

function PositionBearbeiten({ item, mitPreis }: { item: PurchaseOrderItem; mitPreis: boolean }) {
  return (
    <div className="mt-2 grid gap-3 rounded-md border border-line bg-panel px-3 py-2.5">
      <PositionMengeEk item={item} mitPreis={mitPreis} />
      <div className="grid gap-2 sm:grid-cols-3">
        {(["anforderungs_notiz", "reklamation", "reklamation_loesung"] as const).map((feld) => (
          <label key={feld} className="grid gap-1 text-[11px] text-ink-2">
            {feld === "anforderungs_notiz" ? "Anforderung" : feld === "reklamation" ? "Reklamation" : "Lösung"}
            <PositionNotiz item={item} feld={feld} className="min-h-8 text-xs" />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-line-2 pt-2">
        <AnforderungAnGaKnopf item={item} mitText />
        <span className="ml-auto"><PositionLoeschenKnopf item={item} bestaetigen mitText /></span>
      </div>
    </div>
  );
}

export function PositionenPanel({ order, darf, mitPreis, onHinzufuegen }: {
  order: PurchaseOrderFile;
  /** Einkaufsrecht: bearbeiten, Hinweise, loeschen, hinzufuegen. */
  darf: boolean;
  /** Preisrecht: EK aendern (sonst bleibt er ganz aus dem Body). */
  mitPreis: boolean;
  onHinzufuegen: () => void;
}) {
  const [offen, setOffen] = useState<number | null>(null);
  return (
    <section aria-label="Positionen bearbeiten" className="rounded-md border border-line bg-background p-4">
      <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-bold text-ink">Positionen der Bestellung</h3>
        {darf ? <Button type="button" variant="outline" size="sm" onClick={onHinzufuegen}>Positionen hinzufügen</Button> : null}
      </header>
      {order.items.length === 0 ? (
        <p className="text-sm text-ink-2">Noch keine Positionen.</p>
      ) : (
        <ul className="grid divide-y divide-line-2">
          {order.items.map((item) => {
            const name = positionsName(item);
            const auf = offen === item.id;
            return (
              <li key={item.id} className="py-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="w-5 text-xs text-ink-2">{item.position}</span>
                  <GrundartikelLink className="font-mono font-semibold" code={item.merchant_sku} />
                  <span className="min-w-0 flex-1 truncate text-ink">{item.name_snapshot ?? "—"}</span>
                  <AusverkaufAbzeichen {...item} />
                  {item.anforderungs_notiz ? <Chip>Anforderung</Chip> : null}
                  {item.reklamation ? <Chip>Reklamation</Chip> : null}
                  <span className="font-mono">{mengeMitEinheit(item.qty, item.einheit)}</span>
                  {item.unit_price_usd !== null ? (
                    <span className="font-mono text-ink-2">{formatUsd(item.unit_price_usd)}{jeEinheit(item.einheit)}</span>
                  ) : null}
                  {darf ? (
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs"
                      aria-label={`Position ${name} bearbeiten`} aria-expanded={auf}
                      onClick={() => setOffen(auf ? null : item.id)}>
                      {auf ? "Schließen" : "Bearbeiten"}
                    </Button>
                  ) : null}
                </div>
                {auf ? <PositionBearbeiten key={item.id} item={item} mitPreis={mitPreis} /> : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
